import {
  Component,
  JSX,
  Match,
  Show,
  Switch,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
} from 'solid-js'
import { chatStore, i18nStore, toastStore } from '/web/store'
import Convertible from '../../../shared/Mode/Convertible'
import { A, useParams, useSearchParams } from '@solidjs/router'
import { useEditableBots } from '../util'
import { AppSchema } from '/common/types'
import { CreateCharacterForm } from '../../Character/CreateCharacterForm'
import Loading from '/web/shared/Loading'
import { ModeGenSettings } from '../../../shared/Mode/ModeGenSettings'
import MemberModal from '../MemberModal'
import Button from '/web/shared/Button'
import UISettings from '../../Settings/UISettings'
import { wait } from '/common/util'
import CharacterSelect from '/web/shared/CharacterSelect'
import ChatSettings from '../ChatSettings'
import ChatMemoryModal from './MemoryModal'
import { usePaneManager } from '/web/shared/hooks'
import { usePresetContext } from '/web/store/preset-context'

export { ChatPanes as default }

export const useValidChatPane = () => {
  const [search] = useSearchParams()

  const isValidPane = createMemo(() => {
    switch (search.pane) {
      case 'character':
      case 'preset':
      case 'participants':
      case 'ui':
      case 'chat-settings':
      case 'memory':
      case 'other':
        return true

      default:
        return false
    }
  })

  return isValidPane
}

const ChatPanes: Component<{}> = (props) => {
  const i18n = i18nStore((s) => ({ t: s.t }))
  const params = useParams()
  const pane = usePaneManager()
  const [preset, setters] = usePresetContext()

  const chats = chatStore((s) => {
    return {
      ...(s.details[s.lastChatId]?.chat._id === params.id ? s.details[s.lastChatId] : undefined),
    }
  })

  const [paneFooter, setPaneFooter] = createSignal<JSX.Element>()
  const [editId, setEditId] = createSignal<string>()

  // const isValidPane = useValidChatPane()

  onCleanup(() => {
    closeCharEditor(false)
  })

  createEffect(() => {
    setEditId(chats.char?._id ?? '')
  })

  const editableChars = useEditableBots()

  const charBeingEdited = createMemo(() => {
    return editableChars().find((ch) => ch._id === editId())
  })

  const changeEditingChar = async (char: AppSchema.Character | undefined) => {
    const prev = editId()
    if (prev === char?._id) return

    setEditId('')
    if (!char) return
    await wait(0.2)
    setEditId(char._id)
  }

  const closePane = (search = true) => {
    if (search) {
      pane.update()
    }
  }

  const closeCharEditor = (search?: boolean) => {
    closePane(search)
    setEditId(chats.char?._id || '')
  }

  const onPresetChanged = (presetId: string) => {
    if (!chats.chat) return

    setters.change({ chatId: chats.chat._id, presetId })
  }

  return (
    <Show when={pane.showing()}>
      <Switch>
        <Match when={pane.pane() === 'character'}>
          <Convertible title={i18n.t.char_editCharacter} close={closeCharEditor} footer={paneFooter()}>
            <Show when={editId() !== ''}>
              <CreateCharacterForm
                chat={chats.chat}
                editId={editId()}
                footer={setPaneFooter}
                close={closeCharEditor}
                temp={editId()?.startsWith('temp-')}
                onSuccess={() => toastStore.success(i18n.t.chat_temp_char_updated)}
              >
                <Show when={editableChars().length > 1}>
                  <CharacterSelect
                    class="w-full"
                    fieldName="editingId"
                    items={editableChars()}
                    value={charBeingEdited()}
                    onChange={changeEditingChar}
                  />
                </Show>
              </CreateCharacterForm>
            </Show>

            <Show when={editId() === ''}>
              <div class="flex h-full w-full items-center justify-center">
                <Loading />
              </div>
            </Show>
          </Convertible>
        </Match>

        <Match when={pane.pane() === 'preset'}>
          <Convertible close={closePane} footer={paneFooter()}>
            <ModeGenSettings
              page="mode"
              preset={preset.current}
              setters={setters}
              presetId={preset.current._id}
              onPresetChanged={onPresetChanged}
              close={closePane}
              footer={setPaneFooter}
            />
          </Convertible>
        </Match>

        <Match when={pane.pane() === 'memory'}>
          <Convertible
            close={closePane}
            footer={paneFooter()}
            title={
              <A class="link" href="/guides/memory">
                {i18n.t.chat_memory_guide}
              </A>
            }
          >
            <ChatMemoryModal chat={chats.chat!} close={closePane} footer={setPaneFooter} />
          </Convertible>
        </Match>

        <Match when={pane.pane() === 'participants'}>
          <MemberModal show chat={chats.chat!} charId={chats?.char?._id!} close={closePane} />
        </Match>

        <Match when={pane.pane() === 'ui'}>
          <Convertible
            close={closePane}
            title={i18n.t.chat_ui_settings}
            footer={<Button onClick={() => closePane()}>{i18n.t.close}</Button>}
          >
            <UISettings />
          </Convertible>
        </Match>

        <Match when={pane.pane() === 'chat-settings'}>
          <Convertible close={closePane} title={i18n.t.chat_editChat} footer={paneFooter()}>
            <ChatSettings footer={setPaneFooter} close={closePane} />
          </Convertible>
        </Match>
      </Switch>
    </Show>
  )
}
